// centralizedDataService.ts - Centralized data management for continuous scanning
import { ScannerData } from '../types/ScannerData';

export interface DataPacket {
  data: ScannerData[];
  timestamp: number;
  dataMode: 'accumulated' | '5min';
  scanStatus: {
    isScanning: boolean;
    lastScanTime: number;
    symbolsScanned: number;
    totalSymbols: number;
    errors: string[];
  };
  marketStatus: {
    isMarketOpen: boolean;
    marketStartTime: number;
    marketEndTime: number;
    nextScanTime: number;
  };
}

export interface ScanConfig {
  symbols: string[];
  scanInterval: number; // milliseconds
  marketHours: {
    start: string; // "09:30"
    end: string;   // "16:00"
    timezone: string; // "America/New_York"
  };
  polygonApiKey: string;
  maxConcurrentRequests: number;
  retryAttempts: number;
  retryDelay: number;
}

export interface ScanResult {
  success: boolean;
  data: ScannerData[];
  errors: string[];
  scanTime: number;
  symbolsProcessed: number;
}

class CentralizedDataService {
  private dataPacket: DataPacket | null = null;
  private scanConfig: ScanConfig | null = null;
  private isScanning = false;
  private scanInterval: NodeJS.Timeout | null = null;
  private subscribers: ((packet: DataPacket) => void)[] = [];
  private lastScanTime = 0;
  private scanQueue: string[] = [];
  private currentScanIndex = 0;

  // Initialize the service with configuration
  async initialize(config: ScanConfig): Promise<void> {
    this.scanConfig = config;
    console.log('🔧 [CENTRALIZED DATA SERVICE] Initializing with config:', {
      symbolsCount: config.symbols.length,
      scanInterval: config.scanInterval,
      marketHours: config.marketHours
    });

    // Start continuous scanning
    await this.startContinuousScanning();
  }

  // Start continuous scanning process
  private async startContinuousScanning(): Promise<void> {
    if (!this.scanConfig) {
      throw new Error('Service not initialized. Call initialize() first.');
    }

    console.log('🚀 [CENTRALIZED DATA SERVICE] Starting continuous scanning...');
    
    // Check if market is open
    const isMarketOpen = this.isMarketOpen();
    
    if (isMarketOpen) {
      console.log('📈 [CENTRALIZED DATA SERVICE] Market is open, starting immediate scan');
      await this.performScan();
    } else {
      console.log('⏰ [CENTRALIZED DATA SERVICE] Market is closed, scheduling next scan');
      this.scheduleNextScan();
    }

    // Set up continuous scanning interval
    this.scanInterval = setInterval(async () => {
      try {
        const isMarketOpen = this.isMarketOpen();
        
        if (isMarketOpen && !this.isScanning) {
          console.log('🔄 [CENTRALIZED DATA SERVICE] Market open, performing scan');
          await this.performScan();
        } else if (!isMarketOpen) {
          console.log('⏸️ [CENTRALIZED DATA SERVICE] Market closed, pausing scans');
          this.isScanning = false;
        }
      } catch (error) {
        console.error('❌ [CENTRALIZED DATA SERVICE] Error in scan interval:', error);
      }
    }, this.scanConfig.scanInterval);
  }

  // Check if market is currently open
  private isMarketOpen(): boolean {
    if (!this.scanConfig) return false;

    const now = new Date();
    const marketStart = this.parseMarketTime(this.scanConfig.marketHours.start);
    const marketEnd = this.parseMarketTime(this.scanConfig.marketHours.end);
    
    const currentTime = now.getHours() * 60 + now.getMinutes();
    
    return currentTime >= marketStart && currentTime <= marketEnd;
  }

  // Parse market time string (e.g., "09:30") to minutes
  private parseMarketTime(timeStr: string): number {
    const [hours, minutes] = timeStr.split(':').map(Number);
    return hours * 60 + minutes;
  }

  // Schedule next scan when market opens
  private scheduleNextScan(): void {
    if (!this.scanConfig) return;

    const now = new Date();
    const marketStart = this.parseMarketTime(this.scanConfig.marketHours.start);
    const currentTime = now.getHours() * 60 + now.getMinutes();
    
    let nextScanTime: number;
    
    if (currentTime < marketStart) {
      // Market hasn't opened yet today
      nextScanTime = marketStart;
    } else {
      // Market has closed, schedule for tomorrow
      nextScanTime = marketStart + (24 * 60); // Next day
    }
    
    const nextScanDate = new Date();
    nextScanDate.setHours(Math.floor(nextScanTime / 60), nextScanTime % 60, 0, 0);
    
    const delay = nextScanDate.getTime() - now.getTime();
    
    console.log(`⏰ [CENTRALIZED DATA SERVICE] Next scan scheduled in ${Math.round(delay / 1000 / 60)} minutes`);
    
    setTimeout(async () => {
      console.log('🚀 [CENTRALIZED DATA SERVICE] Scheduled scan starting...');
      await this.performScan();
    }, delay);
  }

  // Perform a complete scan of all symbols
  private async performScan(): Promise<void> {
    if (!this.scanConfig || this.isScanning) {
      return;
    }

    this.isScanning = true;
    this.lastScanTime = Date.now();
    
    console.log(`🔍 [CENTRALIZED DATA SERVICE] Starting scan of ${this.scanConfig.symbols.length} symbols`);
    
    const scanResults: ScannerData[] = [];
    const errors: string[] = [];
    let symbolsProcessed = 0;

    try {
      // Process symbols in batches to avoid overwhelming the API
      const batchSize = Math.min(this.scanConfig.maxConcurrentRequests, 10);
      const batches = this.chunkArray(this.scanConfig.symbols, batchSize);
      
      for (const batch of batches) {
        const batchPromises = batch.map(symbol => this.scanSymbol(symbol));
        const batchResults = await Promise.allSettled(batchPromises);
        
        batchResults.forEach((result, index) => {
          if (result.status === 'fulfilled') {
            scanResults.push(...result.value.data);
            symbolsProcessed++;
          } else {
            const error = `Failed to scan ${batch[index]}: ${result.reason}`;
            errors.push(error);
            console.error('❌ [CENTRALIZED DATA SERVICE]', error);
          }
        });
        
        // Small delay between batches to respect API limits
        if (batches.indexOf(batch) < batches.length - 1) {
          await this.delay(100);
        }
      }
      
      // Update data packet
      this.dataPacket = {
        data: scanResults,
        timestamp: Date.now(),
        dataMode: 'accumulated', // Default to accumulated
        scanStatus: {
          isScanning: false,
          lastScanTime: this.lastScanTime,
          symbolsScanned: symbolsProcessed,
          totalSymbols: this.scanConfig.symbols.length,
          errors
        },
        marketStatus: {
          isMarketOpen: this.isMarketOpen(),
          marketStartTime: this.parseMarketTime(this.scanConfig.marketHours.start),
          marketEndTime: this.parseMarketTime(this.scanConfig.marketHours.end),
          nextScanTime: this.lastScanTime + this.scanConfig.scanInterval
        }
      };
      
      // Notify subscribers
      this.notifySubscribers();
      
      console.log(`✅ [CENTRALIZED DATA SERVICE] Scan completed: ${symbolsProcessed}/${this.scanConfig.symbols.length} symbols, ${scanResults.length} records, ${errors.length} errors`);
      
    } catch (error) {
      console.error('❌ [CENTRALIZED DATA SERVICE] Scan failed:', error);
      errors.push(`Scan failed: ${error}`);
    } finally {
      this.isScanning = false;
    }
  }

  // Scan a single symbol
  private async scanSymbol(symbol: string): Promise<ScanResult> {
    if (!this.scanConfig) {
      throw new Error('Service not initialized');
    }

    const startTime = Date.now();
    const errors: string[] = [];
    
    try {
      // Call backend API to get data for this symbol
      const response = await fetch(`/api/scan-symbol/${symbol}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.scanConfig.polygonApiKey}`
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      
      if (result.error) {
        throw new Error(result.error);
      }

      return {
        success: true,
        data: result.data || [],
        errors: [],
        scanTime: Date.now() - startTime,
        symbolsProcessed: 1
      };
      
    } catch (error) {
      const errorMessage = `Failed to scan ${symbol}: ${error}`;
      errors.push(errorMessage);
      
      return {
        success: false,
        data: [],
        errors,
        scanTime: Date.now() - startTime,
        symbolsProcessed: 0
      };
    }
  }

  // Utility function to chunk array into batches
  private chunkArray<T>(array: T[], chunkSize: number): T[][] {
    const chunks: T[][] = [];
    for (let i = 0; i < array.length; i += chunkSize) {
      chunks.push(array.slice(i, i + chunkSize));
    }
    return chunks;
  }

  // Utility function to delay execution
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  // Subscribe to data updates
  subscribe(callback: (packet: DataPacket) => void): () => void {
    this.subscribers.push(callback);
    
    // Return unsubscribe function
    return () => {
      const index = this.subscribers.indexOf(callback);
      if (index > -1) {
        this.subscribers.splice(index, 1);
      }
    };
  }

  // Notify all subscribers
  private notifySubscribers(): void {
    if (this.dataPacket) {
      this.subscribers.forEach(callback => {
        try {
          callback(this.dataPacket!);
        } catch (error) {
          console.error('❌ [CENTRALIZED DATA SERVICE] Error notifying subscriber:', error);
        }
      });
    }
  }

  // Get current data packet
  getCurrentDataPacket(): DataPacket | null {
    return this.dataPacket;
  }

  // Get current data
  getCurrentData(): ScannerData[] {
    return this.dataPacket?.data || [];
  }

  // Get scan status
  getScanStatus() {
    return this.dataPacket?.scanStatus || {
      isScanning: false,
      lastScanTime: 0,
      symbolsScanned: 0,
      totalSymbols: 0,
      errors: []
    };
  }

  // Get market status
  getMarketStatus() {
    return this.dataPacket?.marketStatus || {
      isMarketOpen: false,
      marketStartTime: 0,
      marketEndTime: 0,
      nextScanTime: 0
    };
  }

  // Force a scan (for admin use)
  async forceScan(): Promise<void> {
    console.log('🔄 [CENTRALIZED DATA SERVICE] Force scan requested');
    await this.performScan();
  }

  // Stop scanning
  stopScanning(): void {
    if (this.scanInterval) {
      clearInterval(this.scanInterval);
      this.scanInterval = null;
    }
    this.isScanning = false;
    console.log('⏹️ [CENTRALIZED DATA SERVICE] Scanning stopped');
  }

  // Update configuration
  async updateConfig(newConfig: Partial<ScanConfig>): Promise<void> {
    if (this.scanConfig) {
      this.scanConfig = { ...this.scanConfig, ...newConfig };
      console.log('🔧 [CENTRALIZED DATA SERVICE] Configuration updated');
      
      // Restart scanning with new config
      this.stopScanning();
      await this.startContinuousScanning();
    }
  }

  // Get configuration
  getConfig(): ScanConfig | null {
    return this.scanConfig;
  }

  // Check if service is running
  isRunning(): boolean {
    return this.scanInterval !== null;
  }

  // Get service statistics
  getStatistics() {
    return {
      isRunning: this.isRunning(),
      isScanning: this.isScanning,
      lastScanTime: this.lastScanTime,
      subscribersCount: this.subscribers.length,
      dataPacketAge: this.dataPacket ? Date.now() - this.dataPacket.timestamp : 0,
      totalRecords: this.dataPacket?.data.length || 0
    };
  }
}

// Export singleton instance
export const centralizedDataService = new CentralizedDataService();

// Default configuration
export const DEFAULT_SCAN_CONFIG: ScanConfig = {
  symbols: [
    // Major indices
    'SPY', 'QQQ', 'IWM', 'DIA', 'VTI',
    // Major stocks
    'AAPL', 'MSFT', 'GOOGL', 'AMZN', 'TSLA', 'META', 'NVDA', 'NFLX', 'AMD', 'INTC',
    'JPM', 'BAC', 'WMT', 'PG', 'JNJ', 'UNH', 'HD', 'MA', 'V', 'DIS',
    // ETFs
    'XLF', 'XLK', 'XLE', 'XLI', 'XLV', 'XLY', 'XLU', 'XLP', 'XLB', 'XLRE'
  ],
  scanInterval: 30000, // 30 seconds
  marketHours: {
    start: '09:30',
    end: '16:00',
    timezone: 'America/New_York'
  },
  polygonApiKey: process.env.POLYGON_API_KEY || '',
  maxConcurrentRequests: 5,
  retryAttempts: 3,
  retryDelay: 1000
};
